import { NOOP, isClient } from '@lit-element/utils'

export const raf = isClient
  ? requestAnimationFrame
  : (cb: FrameRequestCallback) => {
      setTimeout(cb, 16)
    }

/**
 * 将一个方法进行节流
 *
 * @param method 需要节流的方法，需自行绑定 this
 * @param interval 节流后的触发间隔，默认 16 ms (1 帧)
 *
 * @returns 节流后的方法
 */
export function throttle<T extends (...args: any[]) => any>(
  method: T,
  interval = 16
): (...args: Parameters<T>) => void {
  if (typeof method !== 'function') {
    return NOOP
  }
  const invoke = (...args: Parameters<T>) => {
    method(...args)
  }

  if (interval <= 0) {
    return debounceMinor(invoke)
  }

  let lastCall = 0
  let timer: ReturnType<typeof setTimeout>

  return function (...args: Parameters<T>) {
    const current = Date.now()
    const elapsed = current - lastCall

    clearTimeout(timer)

    if (elapsed >= interval) {
      lastCall = current
      invoke(...args)
    } else {
      timer = setTimeout(() => {
        lastCall = Date.now()
        invoke(...args)
      }, Math.max(0, interval - elapsed))
    }
  }
}

/**
 * 对给定的方法进行微任务级别的防抖
 *
 * @param method 需要防抖的方法，需自行绑定 this
 *
 * @returns 防抖后的方法
 */
export function debounceMinor<T extends (...args: any[]) => any>(method: T) {
  if (typeof method !== 'function') {
    return method
  }

  let called = false
  let lastArgs: Parameters<T>
  let promise: Promise<Awaited<ReturnType<T>>>

  return function (...args: Parameters<T>) {
    lastArgs = args

    if (!called) {
      called = true
      promise = Promise.resolve().then(() => {
        called = false
        promise = undefined!

        return method(...lastArgs)
      })
    }

    return promise
  }
}

const frameCallbacks = new Set<(...args: any[]) => any>()
const frameArgsMap = new WeakMap<any, any[]>()

function flushFrameCallbacks() {
  frameCallbacks.forEach((fn) => {
    fn(...frameArgsMap.get(fn)!)
  })
  frameCallbacks.clear()
}

/**
 * 在下一渲染帧，仅执行一次传入的方法
 *
 * @param method 需要执行的方法
 * @param args 方法的额外参数，在方法调用前多次传入将会覆盖之前的参数
 */
export function nextFrameOnce<T extends (...args: any[]) => any>(
  method: T,
  ...args: any[]
) {
  if (typeof method !== 'function') {
    return method
  }

  frameArgsMap.set(method, args)

  if (frameCallbacks.has(method)) {
    return
  }

  frameCallbacks.add(method)

  if (frameCallbacks.size === 1) {
    raf(flushFrameCallbacks)
  }
}
